import { Plus, Trash2, PenLine, Eye } from "lucide-react";
import { brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Lbl = ({ t, className = "", children }) => (
  <label className={`flex flex-col gap-1 ${className}`}><span className="text-[11px] font-medium text-muted-foreground">{t}</span>{children}</label>
);

function Preview({ lead, fee, eta, defaultEta, id }) {
  const m = Number(eta) || defaultEta;
  return (
    <p className="flex basis-full items-start gap-1.5 text-xs text-muted-foreground" data-testid={id}>
      <Eye className="mt-px h-3.5 w-3.5 shrink-0" /><span className="shrink-0 whitespace-nowrap">Cliente vê:</span><span className="font-medium text-foreground">{lead} · Taxa {brl(fee)}{m ? ` · chega em ~${m} min` : ""}</span>
    </p>
  );
}

export function RadiusList({ radii, onChange, disabled, defaultEta }) {
  const patch = (i, k, v) => onChange(radii.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="card-surface p-5 space-y-2" data-testid="delivery-radii">
      <p className="eyebrow">Raios a partir da loja</p>
      {disabled && <p className="text-xs text-amber-600 dark:text-amber-400">Defina a localização da loja para usar os raios.</p>}
      {radii.map((r, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2 text-sm">
          <Lbl t="Distância até (km)"><Input type="number" min="0" step="0.5" value={r.km} onChange={(e) => patch(i, "km", e.target.value)} className="w-28" data-testid={`radius-km-${i}`} /></Lbl>
          <Lbl t="Taxa (R$)"><Input type="number" min="0" step="0.5" value={r.fee} onChange={(e) => patch(i, "fee", e.target.value)} className="w-24" data-testid={`radius-fee-${i}`} /></Lbl>
          <Lbl t="Prazo (min)"><Input type="number" min="0" step="5" value={r.eta || ""} placeholder="Padrão" onChange={(e) => patch(i, "eta", e.target.value)} className="w-24" data-testid={`radius-eta-${i}`} /></Lbl>
          <Button size="icon" variant="ghost" onClick={() => onChange(radii.filter((_, j) => j !== i))} data-testid={`radius-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          {Number(r.km) > 0 && <Preview lead={`até ${Number(r.km).toLocaleString("pt-BR")} km`} fee={r.fee} eta={r.eta} defaultEta={defaultEta} id={`radius-preview-${i}`} />}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...radii, { km: "", fee: "", eta: "" }])} data-testid="add-radius-btn"><Plus className="h-4 w-4 mr-1" />Adicionar raio</Button>
    </div>
  );
}

export function AreaList({ areas, editing, onEdit, onPatch, onRemove, onNew, defaultEta }) {
  return (
    <div className="card-surface p-5 space-y-2" data-testid="delivery-areas">
      <p className="eyebrow">Áreas desenhadas no mapa</p>
      {areas.length > 0 && <p className="text-xs text-muted-foreground">Prazo vazio usa o prazo padrão.</p>}
      {areas.map((a, i) => (
        <div key={i} className={`flex flex-wrap items-end gap-2 rounded-lg p-1 ${editing === i ? "bg-primary/10" : ""}`}>
          <div className="flex min-w-0 flex-1 basis-56 items-end gap-2">
          <Lbl t="Cor" className="shrink-0"><input type="color" value={a.color} onChange={(e) => onPatch(i, { color: e.target.value })} className="h-9 w-9 rounded border" data-testid={`area-color-${i}`} /></Lbl>
          <Lbl t="Nome da área" className="min-w-0 flex-1"><Input value={a.name} placeholder="Ex: Centro" onChange={(e) => onPatch(i, { name: e.target.value })} className="min-w-0" data-testid={`area-name-${i}`} /></Lbl>
          </div>
          <div className="flex items-end gap-2">
          <Lbl t="Taxa (R$)" className="shrink-0"><Input type="number" min="0" step="0.5" value={a.fee} onChange={(e) => onPatch(i, { fee: e.target.value })} className="w-20" data-testid={`area-fee-${i}`} /></Lbl>
          <Lbl t="Prazo (min)" className="shrink-0"><Input type="number" min="0" step="5" value={a.eta || ""} placeholder="Padrão" onChange={(e) => onPatch(i, { eta: e.target.value })} className="w-24" data-testid={`area-eta-${i}`} /></Lbl>
          <Lbl t="Pontos" className="w-12 shrink-0"><span className={`flex h-9 items-center text-xs ${a.points.length < 3 ? "text-red-500" : "text-muted-foreground"}`} data-testid={`area-points-${i}`}>{a.points.length} pts</span></Lbl>
          <Button size="icon" variant={editing === i ? "default" : "ghost"} onClick={() => onEdit(editing === i ? null : i)} title="Editar no mapa" data-testid={`area-edit-${i}`}><PenLine className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onRemove(i)} data-testid={`area-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
          {a.name?.trim() && <Preview lead={a.name.trim()} fee={a.fee} eta={a.eta} defaultEta={defaultEta} id={`area-preview-${i}`} />}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={onNew} data-testid="add-area-btn"><Plus className="h-4 w-4 mr-1" />Desenhar nova área</Button>
    </div>
  );
}
